import {CommonModule} from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Self,
    SimpleChanges,
} from '@angular/core';
import {AppValidators, TranslateService} from '@brunata-angular/util';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormsModule,
    NgControl,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {v4 as uuidv4} from 'uuid';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';
import {startWith} from 'rxjs';
import {patchControlMethod} from '../patch-control-method';

@Component({
    selector: 'app-input-email-list',
    imports: [
        CommonModule,
        ControlMessagesComponent,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        PopoverIconComponent,
    ],
    templateUrl: './input-email-list.component.html',
    styleUrl: './input-email-list.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputEmailListComponent implements OnChanges, OnInit, OnDestroy, ControlValueAccessor {
    private readonly translateService = inject(TranslateService);
    @Input() information: string | undefined;
    @Input() label = this.translateService.translate('monitoring_email');
    @Input() id: string = uuidv4();

    @Input() maxItems: number | undefined;
    @Input() maxItemsLabel: string | undefined;

    // Parameterized component labels and styles to support multiple applications
    @Input() addButtonLabel: string | undefined;
    // Default Online styling
    @Input() addButtonClass = 'btn btn-text-primary mt-3';
    @Input() removeButtonClass = 'btn btn-link text-danger email-delete-hover';

    @Self() protected readonly ngControl = inject(NgControl);

    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    /** Wins over the `Validators.required` detection when set, like InputTextGroupComponent. */
    @Input({transform: booleanAttribute}) required: boolean | undefined;

    private isRequired = false;

    private onChange: ((value: string[] | null) => void) | undefined;

    private restoreMarkAllAsTouched: (() => void) | undefined;
    protected onTouch: (() => void) | undefined;

    protected readonly formArray = this.formBuilder.array<string>([]);

    constructor() {
        this.ngControl.valueAccessor = this;
    }

    get labelClass(): string {
        return `${this.isRequired ? 'required' : ''}`;
    }

    protected get canAddItem(): boolean {
        return this.maxItems === undefined || this.formArray.length < this.maxItems;
    }

    protected get exceedsMaxItems(): boolean {
        return this.maxItems !== undefined && this.nonEmptyEmails.length > this.maxItems;
    }

    private get nonEmptyEmails(): string[] {
        return this.formArray.value.filter(email => email.trim().length > 0);
    }

    get addEmailLabel(): string {
        return (
            this.addButtonLabel ??
            this.translateService.translate('titles_addAnotherEmail', 'online', 'monitoringAlarms')
        );
    }

    private static allEmailsEmptyValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!(control instanceof FormArray)) {
                return null;
            }

            const allEmpty = control
                .getRawValue()
                .every(value => typeof value === 'string' && value.trim() === '');

            return allEmpty ? {required: true} : null;
        };
    }

    protected addItem(email: string = ''): void {
        const validators = [AppValidators.email];

        this.changeDetectorRef.markForCheck();
        this.formArray.push(this.formBuilder.control<string>(email, validators), {
            emitEvent: false,
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('required' in changes) {
            this.applyRequired();
        }
    }

    ngOnInit(): void {
        this.applyRequired();
        this.ngControl.control?.addValidators(this.boundMaxItemsValidator);

        /*  Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
                 touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
                 listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
                 markAsTouched() method in order to call detectChanges(). */
        const control = this.ngControl.control;
        if (control != null) {
            this.restoreMarkAllAsTouched = patchControlMethod(control, 'markAllAsTouched', () => {
                this.formArray.markAsTouched();
                this.formArray.controls.forEach(item => item.markAsTouched());
                this.changeDetectorRef.markForCheck();
            });
        }

        this.formArray.valueChanges
            .pipe(startWith([]), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateParentValue());
    }

    /** Angular's `cleanUpControl` does not remove the validator added above. (AGI-19494) */
    ngOnDestroy(): void {
        this.restoreMarkAllAsTouched?.();

        const control = this.ngControl.control;
        if (control == null) {
            return;
        }
        control.removeValidators(this.boundMaxItemsValidator);
        control.updateValueAndValidity({emitEvent: false});
    }

    /** One stable reference: `removeValidators` matches by identity. */
    private readonly boundMaxItemsValidator: ValidatorFn = () =>
        this.exceedsMaxItems ? {maxItems: true} : null;

    private applyRequired(): void {
        this.isRequired =
            this.required ?? this.ngControl.control?.hasValidator(Validators.required) ?? false;

        if (this.isRequired) {
            this.formArray.setValidators([InputEmailListComponent.allEmailsEmptyValidator()]);
        } else {
            this.formArray.clearValidators();
        }
        this.formArray.updateValueAndValidity({emitEvent: false});
        this.changeDetectorRef.markForCheck();
    }

    private updateParentValue(): void {
        if (!this.onChange) {
            return;
        }

        this.applyRequired();

        const nonEmptyEmails = this.nonEmptyEmails;

        const hasInvalidEmail = this.formArray.controls.some(
            control => control.value.trim().length > 0 && control.invalid,
        );

        if (this.formArray.value.length === 0) {
            this.addItem();
            return;
        }

        if (hasInvalidEmail) {
            this.onChange(null);
            this.ngControl.control?.setErrors({invalidEmail: true});
        } else if (this.isRequired && nonEmptyEmails.length === 0) {
            this.onChange(null);
            this.ngControl.control?.setErrors({required: true});
        } else {
            this.onChange(nonEmptyEmails);
            this.ngControl.control?.setErrors(this.exceedsMaxItems ? {maxItems: true} : null);
        }
    }

    registerOnChange(fn: (value: string[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = (): void => {
            this.formArray.markAsTouched();
            this.formArray.controls.forEach(control => control.markAsTouched());
            fn();
        };
    }

    writeValue(emails: string[] | null): void {
        this.formArray.clear({emitEvent: false});

        if (emails === null || emails.length === 0) {
            this.addItem();
            return;
        }

        emails.forEach(email => this.addItem(email));
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formArray.disable({emitEvent: false});
            return;
        }

        this.formArray.enable({emitEvent: false});
    }

    remove(index: number): void {
        if (this.formArray.length === 1) {
            this.formArray.controls[0].setValue('');
        }
        this.formArray.removeAt(index);
    }
}
